import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plug2, RefreshCw, Unplug, ShieldCheck, AlertCircle, Loader2, Settings as SettingsIcon, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import IntegrationsSetupWizard from "./IntegrationsSetupWizard";

/**
 * IntegrationsPanel — Phase 1.
 *
 * Lists every available accounting provider returned by /api/integrations/providers.
 * "active" providers get a Connect button that initiates OAuth in a full-page redirect.
 * "coming_soon" providers render a disabled grey card.
 * Connected providers show company name, connected date, Disconnect button.
 */
export default function IntegrationsPanel({ currentUserName = "" }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [setupStatuses, setSetupStatuses] = useState({}); // {provider_key: {setup_complete: bool}}
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardProvider, setWizardProvider] = useState("quickbooks");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/integrations/providers");
      const list = r.data.providers || [];
      setProviders(list);
      // For every connected provider, fetch its config so we know if setup is complete
      const setups = {};
      await Promise.all(list.filter(p => p.connection).map(async p => {
        try {
          const cfg = await api.get(`/integrations/${p.key}/config`).then(rr => rr.data);
          setups[p.key] = { setup_complete: !!cfg.setup_complete, company_country: cfg.company_country || "" };
        } catch { setups[p.key] = { setup_complete: false }; }
      }));
      setSetupStatuses(setups);
    } catch (e) {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Surface OAuth round-trip results from URL params
  useEffect(() => {
    if (searchParams.get("qbo_connected") === "1") {
      toast.success("QuickBooks connected successfully");
      const next = new URLSearchParams(searchParams);
      next.delete("qbo_connected");
      setSearchParams(next, { replace: true });
      // After a fresh connect, auto-open the setup wizard once data is loaded.
      refresh().then(() => {
        setWizardProvider("quickbooks");
        setWizardOpen(true);
      });
    }
    const err = searchParams.get("qbo_error");
    if (err) {
      toast.error(`QuickBooks connection failed: ${err}`);
      const next = new URLSearchParams(searchParams);
      next.delete("qbo_error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refresh]);

  const handleConnect = (key) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    // Full-page navigation so the browser follows Intuit's redirect chain.
    window.location.href = `${apiBase}/api/integrations/${key}/connect?connected_by=${encodeURIComponent(currentUserName || "")}`;
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;
    setBusy(true);
    try {
      await api.post(`/integrations/${disconnectTarget.key}/disconnect`, { disconnected_by: currentUserName });
      toast.success(`${disconnectTarget.display_name} disconnected`);
      setDisconnectTarget(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--text-muted)" }} />
        <span className="ml-2 text-sm" style={{ color: "var(--text-muted)" }}>Loading integrations…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="integrations-panel">
      <div className="ui-surface p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
          <div>
            <h2 className="ui-section-title mb-1">Accounting Integrations</h2>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Connect SION to your accounting software so invoices, expenses, contractor pay, and refunds sync automatically.
              SION remains the single source of truth — data only flows out, never back in (except for read-only reconciliation).
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {providers.map(p => (
          <ProviderCard
            key={p.key}
            provider={p}
            setupStatus={setupStatuses[p.key]}
            onConnect={() => handleConnect(p.key)}
            onDisconnect={() => setDisconnectTarget(p)}
            onConfigure={() => { setWizardProvider(p.key); setWizardOpen(true); }}
          />
        ))}
      </div>

      <IntegrationsSetupWizard
        provider={wizardProvider}
        currentUserName={currentUserName}
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={refresh}
      />

      {/* Disconnect confirmation */}
      <Dialog open={!!disconnectTarget} onOpenChange={(o) => !o && setDisconnectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" style={{ color: "var(--danger)" }} />
              Disconnect {disconnectTarget?.display_name}?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block">Disconnecting will stop syncing future transactions to {disconnectTarget?.display_name}.</span>
              <span className="block">Existing records already in {disconnectTarget?.display_name} will not be deleted or affected.</span>
              <span className="block">Sync history and audit logs are kept for 12 months. You can reconnect at any time.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectTarget(null)} disabled={busy} data-testid="cancel-disconnect">Cancel</Button>
            <Button onClick={handleDisconnect} disabled={busy} style={{ background: "var(--danger)", color: "#fff" }} data-testid="confirm-disconnect">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Unplug className="w-4 h-4 mr-1" />}
              {busy ? "Disconnecting…" : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProviderCard({ provider, setupStatus, onConnect, onDisconnect, onConfigure }) {
  const isConnected = !!provider.connection;
  const isComingSoon = provider.status === "coming_soon";
  const setupComplete = isConnected && !!setupStatus?.setup_complete;
  return (
    <div
      className="ui-surface p-5 flex flex-col"
      style={{ opacity: isComingSoon ? 0.6 : 1 }}
      data-testid={`provider-card-${provider.key}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base font-bold">{provider.display_name}</span>
            {isConnected && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full" style={{ background: "rgba(22,163,74,0.15)", color: "#15803D" }}>
                Connected
              </span>
            )}
            {isComingSoon && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full" style={{ background: "rgba(100,116,139,0.18)", color: "var(--text-muted)" }}>
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>{provider.tagline}</p>
        </div>
      </div>

      {isConnected && (
        <div className="mt-3 mb-4 p-3 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
          <div className="text-xs space-y-1">
            <div><span style={{ color: "var(--text-muted)" }}>Company:</span> <span className="font-semibold">{provider.connection.company_name || provider.connection.realm_id}</span></div>
            <div><span style={{ color: "var(--text-muted)" }}>Country:</span> <span>{provider.connection.country || "—"}</span> · <span style={{ color: "var(--text-muted)" }}>Currency:</span> <span>{provider.connection.currency || "—"}</span></div>
            <div><span style={{ color: "var(--text-muted)" }}>Connected:</span> <span>{formatTs(provider.connection.connected_at)}</span> {provider.connection.connected_by && <span style={{ color: "var(--text-muted)" }}>by {provider.connection.connected_by}</span>}</div>
            <div className="flex items-center gap-2 pt-1">
              {setupComplete ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: "rgba(22,163,74,0.15)", color: "#15803D" }}>
                  <CheckCircle2 className="w-3 h-3" /> Sync configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: "rgba(245,158,11,0.15)", color: "#92400E" }}>
                  <AlertCircle className="w-3 h-3" /> Setup not complete
                </span>
              )}
              {provider.connection.environment === "sandbox" && (
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#B45309" }}>Sandbox</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-auto pt-3">
        {isComingSoon && (
          <Button disabled className="w-full" data-testid={`btn-coming-soon-${provider.key}`}>
            <Plug2 className="w-4 h-4 mr-1" /> Coming Soon
          </Button>
        )}
        {!isComingSoon && !isConnected && (
          <Button
            onClick={onConnect}
            className="w-full font-semibold"
            style={{ background: "#2DA01C", color: "#fff" }}
            data-testid={`btn-connect-${provider.key}`}
          >
            <Plug2 className="w-4 h-4 mr-1" /> Connect {provider.display_name}
          </Button>
        )}
        {isConnected && (
          <>
            {!setupComplete ? (
              <Button
                onClick={onConfigure}
                className="w-full font-semibold"
                style={{ background: "var(--primary)", color: "#fff" }}
                data-testid={`btn-setup-${provider.key}`}
              >
                <SettingsIcon className="w-4 h-4 mr-1" /> Complete Setup
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={onConfigure} className="flex-1" data-testid={`btn-mappings-${provider.key}`}>
                  <SettingsIcon className="w-4 h-4 mr-1" /> Mappings
                </Button>
                <Button variant="outline" disabled className="flex-1" data-testid={`btn-sync-${provider.key}`} title="Available in Phase 3">
                  <RefreshCw className="w-4 h-4 mr-1" /> Sync Now
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={onDisconnect}
              className="flex-1"
              style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
              data-testid={`btn-disconnect-${provider.key}`}
            >
              <Unplug className="w-4 h-4 mr-1" /> Disconnect
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}
